import { useNavigate } from 'react-router-dom';
import usePermission from '../../hooks/usePermission';
import React, { useEffect, useMemo } from 'react';
import {
  HeaderSection,
  AppTable,
  useFetchList,
  PAGE_SIZE_OPTIONS,
  IAppTableColumn,
  BaseResponsePaginationProps,
} from '@qlibs/react-components';
import { Actions, IAction } from '@qlibs/react-components/dist/Table';
import ListFilterRow from '../../components/ListFilterRow';
import { readListFilter, readListPage } from '../../helpers/listFilterQuery';
import {
  Button,
  Dropdown,
  Grid,
  Image,
  MenuProps,
  Modal,
  Tag,
  Tooltip,
  message,
} from 'antd';
import { AssetProperties } from './types/asset.type';
import { httpRequest, httpRequestMultipart } from '../../helpers/api';
import { EDateFormat } from '../../const/config';
import { CompanyProperties } from '../company/types/company.type';
import LinkTable from '../../components/LinkTable';
import useGroup from '../../zustand/useGroup';
import {
  EFormatCurrentDate,
  formatCurrentDate,
} from '../../helpers/formatCurrentDate';
import { isRamus2AssetId } from '../../helpers/ramus2';
import styled from 'styled-components';
import {
  RiBuilding4Line,
  RiEqualizer2Line,
  RiMore2Fill,
  RiMore2Line,
} from '@remixicon/react';
import { DeviceProperties } from '../device/types/device.type';
import {
  LeftOutlined,
  PlusOutlined,
  RightOutlined,
  SearchOutlined,
  UploadOutlined,
  WarningFilled,
} from '@ant-design/icons';
import { EAEFuelType } from '../device/Create';
import { useAuthUser } from 'react-auth-kit';
import { ULTRA_SUPER_ADMIN_ROLE_ID } from '../../const/config';
import {
  AssetMobileCard,
  AssetMobileCardBody,
  AssetMobileCardMain,
  AssetMobileCardSubtitle,
  AssetMobileCardTitle,
  AssetMobileDetailButton,
  AssetMobileEmptyState,
  AssetMobileFilterActions,
  AssetMobileFilterApplyButton,
  AssetMobileFilterButton,
  AssetMobileFilterDrawer,
  AssetMobileFilterField,
  AssetMobileFilterFields,
  AssetMobileFilterForm,
  AssetMobileFilterRow,
  AssetMobileFilterSelect,
  AssetMobileIconButton,
  AssetMobileInfoGrid,
  AssetMobileInfoItem,
  AssetMobileInfoLabel,
  AssetMobileInfoValue,
  AssetMobileList,
  AssetMobileMoreButton,
  AssetMobilePage,
  AssetMobilePageButton,
  AssetMobilePagination,
  AssetMobilePaginationActions,
  AssetMobilePaginationInfo,
  AssetMobileSearch,
  AssetMobileThumb,
  MobileAssetHeader,
} from './mobile';
const moment = require('moment');

const FEATURE = 'ASSET';
const allowedImageMimes = ['image/jpeg', 'image/png'];

const Asset = () => {
  const navigate = useNavigate();
  const screens = Grid.useBreakpoint();
  const isMobile = screens.md === false;
  const { isUserHasPermission } = usePermission();

  const handleCreateAsset = () => {
    navigate({ pathname: '/asset/add' });
  };

  const [isModalDeleteOpen, setIsModalDeleteOpen] = React.useState(false);
  const [isModalRestoreOpen, setIsModalRestoreOpen] = React.useState(false);
  const [isMobileFilterOpen, setIsMobileFilterOpen] = React.useState(false);
  const [mobileFilterValues, setMobileFilterValues] = React.useState({
    company: 'all',
    status: 'all',
    filterData: 'default',
  });
  const [currentData, setCurrentData] = React.useState<AssetProperties>();
  const [dataCompany, setDataCompany] = React.useState<CompanyProperties[]>([]);
  const [isLoadingCompanies, setIsLoadingCompanies] = React.useState(true);
  const [forceDeleteId, setForceDeleteId] = React.useState<string>();
  const [uploadTargetAsset, setUploadTargetAsset] =
    React.useState<AssetProperties>();
  const [isUploadingImage, setIsUploadingImage] = React.useState(false);
  const uploadInputRef = React.useRef<HTMLInputElement>(null);
  const uploadTargetAssetRef = React.useRef<AssetProperties | undefined>(
    undefined,
  );

  const { company } = useGroup();
  const auth = useAuthUser();
  const isUltraSuperAdmin =
    auth()?.role?.roleId === ULTRA_SUPER_ADMIN_ROLE_ID;

  const {
    isLoading,
    data,
    pagination,
    query,
    setData,
    setQuery,
    changePage,
    changeLimit,
    handleSearch,
    pageQueries,
    filterDropdown,
  } = useFetchList<AssetProperties>({
    httpRequest: httpRequest as any,
    endpoint: 'asset',
    limit: +PAGE_SIZE_OPTIONS[1],
    initialQuery: {
      filterCompanyIds: readListFilter(
        'filterCompanyIds',
        isUltraSuperAdmin ? '' : company?.companyId || '',
      ),
      filterStatus: readListFilter('filterStatus'),
      filterArchived: readListFilter('filterArchived'),
      search: readListFilter('search'),
    },
    pageQuery: {
      defaultValue: {
        page: readListPage(),
        filterCompanyIds: '',
        filterStatus: '',
        filterArchived: '',
        search: '',
      },
    },
  });

  async function deleteData() {
    try {
      if (currentData) {
        const forceDelete =
          forceDeleteId && forceDeleteId === currentData.assetId;
        await httpRequest.delete('/asset/' + currentData.assetId, {
          params: {
            forceDelete: forceDelete || undefined,
          },
        });
        message.success(
          `Success ${forceDelete ? 'delete' : 'archive'} ${currentData.name}`,
        );

        setQuery({
          ...query,
        });
      } else {
        message.error('Current data not found');
      }
    } catch (error: any) {
      message.error(error.data.message);
    } finally {
      setIsModalDeleteOpen(false);
      setCurrentData(undefined);
    }
  }

  async function restoreData() {
    try {
      if (currentData) {
        await httpRequest.patch('/asset/' + currentData.assetId + '/restore');
        message.success(`Success restore ${currentData.name}`);

        setQuery({
          ...query,
        });
      } else {
        message.error('Current data not found');
      }
    } catch (error: any) {
      message.error(error.data.message);
    } finally {
      setIsModalRestoreOpen(false);
      setCurrentData(undefined);
      setForceDeleteId(undefined);
    }
  }

  async function handleRestoreAsset(id: any) {
    setIsModalRestoreOpen(true);
    const found = data.find((item) => item.assetId === id);
    if (found) {
      setCurrentData(found);
    }
  }

  async function handleDeleteAsset(id: any, forced?: boolean) {
    setIsModalDeleteOpen(true);
    const found = data.find((item) => item.assetId === id);
    if (found) {
      setCurrentData(found);
    }

    if (forced) {
      setForceDeleteId(id);
    }
  }

  function handleCancelDelete() {
    setIsModalDeleteOpen(false);
    setIsModalRestoreOpen(false);
    setCurrentData(undefined);
  }

  function isArchived(id?: string) {
    if (id) {
      const found = data.find((item) => item.assetId === id && item.deletedAt);
      if (found) {
        return true;
      }
    }

    return false;
  }

  function isReadOnlyRamus2Asset(asset?: AssetProperties) {
    return !!asset && (asset.isRamus2 || isRamus2AssetId(asset.assetId));
  }

  function handleStartUploadImage(asset: AssetProperties) {
    if (isUploadingImage) {
      return;
    }

    setUploadTargetAsset(asset);
    uploadTargetAssetRef.current = asset;
    uploadInputRef.current?.click();
  }

  async function handleUploadImageChange(
    event: React.ChangeEvent<HTMLInputElement>,
  ) {
    const file = event.target.files?.[0];
    event.target.value = '';

    const targetAsset = uploadTargetAssetRef.current || uploadTargetAsset;

    if (!file || !targetAsset) {
      return;
    }

    if (!allowedImageMimes.includes(file.type)) {
      message.error('You can only upload JPG or PNG images');
      return;
    }

    if (file.size > 2000000) {
      message.error('Max. image size are 2 Mb');
      return;
    }

    try {
      setIsUploadingImage(true);
      const formData = new FormData();
      formData.append('image', file);

      await httpRequestMultipart.post(
        '/asset/' + targetAsset.assetId + '/upload-image',
        formData,
      );

      message.success(`Success upload image ${targetAsset.name}`);
      setQuery({
        ...query,
      });
    } catch (error: any) {
      message.error(error?.data?.message || 'Failed upload asset image');
    } finally {
      setIsUploadingImage(false);
      setUploadTargetAsset(undefined);
      uploadTargetAssetRef.current = undefined;
    }
  }

  useEffect(() => {
    const fetchCompany = async () => {
      try {
        setIsLoadingCompanies(true);
        const res =
          await httpRequest.get<BaseResponsePaginationProps<CompanyProperties>>(
            '/company',
          );
        if (res.data.payload && res.data.payload.results.length) {
          setDataCompany(res.data.payload.results);
        }
      } catch (error: any) {
        message.error(error.data.message);
      } finally {
        setIsLoadingCompanies(false);
      }
    };

    fetchCompany();
  }, []);

  const filterCompanyOptions = React.useMemo(() => {
    const data = dataCompany.map((item) => ({
      value: item.companyId,
      label: item.name,
    }));
    return [{ value: 'all', label: 'All' }, ...data];
  }, [dataCompany]);

  const selectedCompanyFilter =
    pageQueries.filterCompanyIds ||
    (isUltraSuperAdmin ? 'all' : company?.companyId || 'all');
  const visibleCompanyFilter =
    !isLoadingCompanies &&
    filterCompanyOptions.some(
      (option) => option.value === selectedCompanyFilter,
    )
      ? selectedCompanyFilter
      : undefined;

  const getAssetActions = (record: AssetProperties): IAction[] => {
    const isReadOnlyRamus2 = isReadOnlyRamus2Asset(record);

    return [
      isUserHasPermission([FEATURE + '.DETAIL'])
        ? {
            key: 'detail',
            label: 'Detail',
            onPress: (id?: string) => navigate('/asset/' + id),
          }
        : undefined,
      isReadOnlyRamus2 && isUserHasPermission([FEATURE + '.UPDATE'])
        ? {
            key: 'upload-image',
            label: isUploadingImage ? 'Uploading...' : 'Upload Image',
            disabled: () => isUploadingImage,
            onPress: () => handleStartUploadImage(record),
          }
        : undefined,
      !isReadOnlyRamus2 && isUserHasPermission([FEATURE + '.UPDATE'])
        ? {
            key: 'update',
            label: 'Update',
            onPress: (id?: string) => navigate('/asset/' + id + '/edit'),
          }
        : undefined,
      !isReadOnlyRamus2 && isUserHasPermission([FEATURE + '.DELETE'])
        ? {
            key: 'archive',
            label: 'Delete',
            color: '#ff4d4f',
            disabled: (id?: string) => isArchived(id),
            disabledTooltip: 'This data is already archived',
            onPress: (id?: string) => handleDeleteAsset(id),
          }
        : undefined,
      !isReadOnlyRamus2 && isUserHasPermission([FEATURE + '.RESTORE'])
        ? {
            key: 'restore',
            label: 'Restore',
            color: '#1890ff',
            disabled: (id?: string) => !isArchived(id),
            disabledTooltip: 'This data not in archived',
            onPress: (id?: string) => handleRestoreAsset(id),
          }
        : undefined,
      !isReadOnlyRamus2 && isUserHasPermission([FEATURE + '.FORCE_DELETE'])
        ? {
            key: 'delete',
            label: 'Force Delete',
            onPress: (id?: string) => handleDeleteAsset(id, true),
          }
        : undefined,
    ].filter(Boolean) as IAction[];
  };

  const columns: IAppTableColumn<AssetProperties>[] = [
    {
      title: 'IMAGE',
      dataIndex: 'imageLink',
      key: 'imageLink',
      render: (value, _) => (
        <Image
          src={value}
          width={40}
          height={40}
          style={{
            objectFit: 'contain',
            background: '#f4f6f9',
            borderRadius: 4,
          }}
          fallback='/images/flag-not-found.png'
        />
      ),
    },
    {
      title: 'NAME',
      dataIndex: 'name',
      key: 'name',
      type: 'detail',
      keyId: 'assetId',
      render: (value, records) => (
        <LinkTable onPress={() => navigate('/asset/' + records.assetId)}>
          {records.deletedAt ? (
            <Tag color='#ff5500' className='mb-2'>
              Archived
            </Tag>
          ) : (
            false
          )}
          {value}
        </LinkTable>
      ),
    },
    {
      title: 'COMPANY',
      dataIndex: 'companyId',
      key: 'companyId',
      render: (_, records) =>
        records.company ? (
          <div>
            <div>{records.company.name}</div>
            <div>{records.company.group?.name}</div>
          </div>
        ) : (
          'N/A'
        ),
    },
    {
      title: 'STATUS',
      dataIndex: 'status',
      key: 'status',
      render: (value, _) => (
        <Tag color={value === 'active' ? 'success' : 'error'}>
          {value.toUpperCase()}
        </Tag>
      ),
    },
    {
      title: 'TYPE',
      dataIndex: 'assetType',
      key: 'assetType',
    },
    {
      title: 'DEVICES',
      dataIndex: 'devices',
      key: 'devices',
      render: (_, records) => records.devices?.length,
    },
    {
      title: 'CATEGORY',
      dataIndex: 'assetCategory',
      key: 'assetCategory',
    },
    {
      title: 'CREATED AT',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (value, _) =>
        formatCurrentDate(value, EFormatCurrentDate.DEFAULT_SHORT_MONTH),
    },
    {
      title: 'UPDATED AT',
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      render: (value, _) =>
        formatCurrentDate(value, EFormatCurrentDate.DEFAULT_SHORT_MONTH),
    },
    {
      title: 'ACTION',
      key: 'action',
      render: (_, record) => {
        const actions = getAssetActions(record);

        if (!actions.length) {
          return null;
        }

        return <Actions id={record.assetId} actions={actions} />;
      },
    },
  ];

  const filterData: any = [
    {
      type: 'search',
      key: 'search',
      label: 'Search',
      placeholder: 'Search by asset name',
      onChange: (value: any) => {
        handleSearch(value);
      },
      colSpan: isUserHasPermission([FEATURE + '.DELETE']) ? 6 : 10,
    },
    {
      type: 'select',
      key: 'company',
      label: 'Company',
      options: filterCompanyOptions,
      value: pageQueries.filterCompanyIds || 'All',
      onChange: (value: any) => {
        filterDropdown({
          filterCompanyIds: value === 'all' ? '' : value,
        });
      },
      colSpan: 8,
    },
    {
      type: 'select',
      key: 'status',
      label: 'Status',
      options: [
        {
          value: 'all',
          label: 'All',
        },
        {
          value: 'active',
          label: 'Active',
        },
        {
          value: 'inactive',
          label: 'Inactive',
        },
      ],
      onChange: (value: any) => {
        filterDropdown({
          filterStatus: value === 'all' ? '' : value,
        });
      },
      colSpan: 6,
    },
    {
      type: 'select',
      key: 'filterData',
      label: 'Filter Data',
      options: [
        {
          value: 'default',
          label: 'Default',
        },
        {
          value: 'show',
          label: 'Include Archive Data',
        },
      ],
      onChange: (value: any) => {
        filterDropdown({
          filterArchived: value === 'default' ? '' : value,
        });
      },
      colSpan: 4,
    },
  ].filter((item) =>
    !isUserHasPermission([FEATURE + '.DELETE'])
      ? item.key !== 'filterData'
      : item,
  );

  const mobilePagination = {
    current: Number((pagination as any)?.page || pageQueries.page || 1),
    pageSize: Number(
      (pagination as any)?.limit || query.limit || +PAGE_SIZE_OPTIONS[1],
    ),
    total: Number((pagination as any)?.total || data.length),
  };
  const mobileCurrentPage = mobilePagination.current;
  const mobilePageSize = mobilePagination.pageSize;
  const mobileTotal = mobilePagination.total;
  const mobileTotalPages = Math.max(1, Math.ceil(mobileTotal / mobilePageSize));
  const mobileStartResult =
    mobileTotal === 0 ? 0 : (mobileCurrentPage - 1) * mobilePageSize + 1;
  const mobileEndResult = Math.min(
    mobileCurrentPage * mobilePageSize,
    mobileTotal,
  );

  const handleMobilePageChange = (nextPage: number) => {
    if (nextPage < 1 || nextPage > mobileTotalPages) {
      return;
    }

    changePage(nextPage, mobilePageSize);
  };

  React.useEffect(() => {
    if (!isMobileFilterOpen) {
      return;
    }

    setMobileFilterValues({
      company:
        (pageQueries as any).company ||
        (pageQueries as any).filterCompanyIds ||
        selectedCompanyFilter ||
        'all',
      status:
        (pageQueries as any).status ||
        (pageQueries as any).filterStatus ||
        'all',
      filterData:
        (pageQueries as any).filterData ||
        (pageQueries as any).filterArchived ||
        'default',
    });
  }, [isMobileFilterOpen, pageQueries, selectedCompanyFilter]);

  const handleApplyMobileFilter = () => {
    filterDropdown({
      filterCompanyIds:
        mobileFilterValues.company === 'all' ? '' : mobileFilterValues.company,
      filterStatus:
        mobileFilterValues.status === 'all' ? '' : mobileFilterValues.status,
      filterArchived:
        mobileFilterValues.filterData === 'default'
          ? ''
          : mobileFilterValues.filterData,
    });
    setIsMobileFilterOpen(false);
  };

  // isUserHasPermission([FEATURE + '.DETAIL'])
  //         ? {
  //             key: 'detail',
  //             label: 'Detail',
  //             onPress: (id) => navigate('/asset/' + id),
  //           }
  //         : undefined,
  //       isUserHasPermission([FEATURE + '.UPDATE'])
  //         ? {
  //             key: 'update',
  //             label: 'Update',
  //             onPress: (id) => navigate('/asset/' + id + '/edit'),
  //           }
  //         : undefined,
  //       isUserHasPermission([FEATURE + '.DELETE'])
  //         ? {
  //             key: 'archive',
  //             label: 'Delete',
  //             color: '#ff4d4f',
  //             disabled: (id) => isArchived(id),
  //             disabledTooltip: 'This data is already archived',
  //             onPress: (id) => handleDeleteAsset(id),
  //           }
  //         : undefined,
  //       isUserHasPermission([FEATURE + '.RESTORE'])
  //         ? {
  //             key: 'restore',
  //             label: 'Restore',
  //             color: '#1890ff',
  //             disabled: (id) => !isArchived(id),
  //             disabledTooltip: 'This data not in archived',
  //             onPress: (id) => handleRestoreAsset(id),
  //           }
  //         : undefined,
  //       isUserHasPermission([FEATURE + '.FORCE_DELETE'])
  //         ? {
  //             key: 'delete',
  //             label: 'Force Delete',
  //             onPress: (id) => handleDeleteAsset(id, true),
  //           }
  //         : undefined,

  // const handleMenuClick = (asset: AssetProperties) => (menuItem: MenuProps['onClick']) =>{
  //   if (menuItem?.key === 'update') {
  //     alert('update');
  //   } else if (menuItem?.key === 'delete') {
  //     alert('delete');
  //   } else if (menuItem?.key === 'restore') {
  //     alert('restore');
  //   } else if (menuItem?.key === 'force-delete') {
  //     alert('force-delete');
  //   }
  // };

  const handleMenuClick =
    (asset: AssetProperties) =>
    (info: MenuProps['onClick'] extends (arg: infer I) => any ? I : any) => {
      if (info?.key === 'update') {
        navigate('/asset/' + asset.assetId + '/edit');
      } else if (info?.key === 'upload-image') {
        handleStartUploadImage(asset);
      } else if (info?.key === 'delete') {
        handleDeleteAsset(asset.assetId);
      } else if (info?.key === 'restore') {
        handleRestoreAsset(asset.assetId);
      } else if (info?.key === 'force-delete') {
        handleDeleteAsset(asset.assetId, true);
      }
    };

  const items = useMemo<MenuProps['items']>(() => {
    const allItems = [
      { key: 'update', label: 'Update', permission: FEATURE + '.UPDATE' },
      { key: 'delete', label: 'Delete', permission: FEATURE + '.DELETE' },
      { key: 'restore', label: 'Restore', permission: FEATURE + '.RESTORE' },
      {
        key: 'force-delete',
        label: 'Force Delete',
        danger: true,
        permission: FEATURE + '.FORCE_DELETE',
      },
    ];

    return allItems.filter((item) => isUserHasPermission([item.permission]));
  }, []);

  const getMenuItems = (asset: AssetProperties): MenuProps['items'] => {
    if (isReadOnlyRamus2Asset(asset)) {
      return isUserHasPermission([FEATURE + '.UPDATE'])
        ? [
            {
              key: 'upload-image',
              label: isUploadingImage ? 'Uploading...' : 'Upload Image',
              icon: <UploadOutlined />,
              disabled: isUploadingImage,
            },
          ]
        : [];
    }

    if (items) {
      return items.filter((item) => {
        if (item && item.key === 'restore' && !isArchived(asset.assetId)) {
          return false;
        }
        return true;
      });
    }
  };

  const _renderDeviceAlert = (devices?: DeviceProperties[]) => {
    const foundAENoMetadata = devices?.filter(
      (device) =>
        device.name.toLowerCase().includes('ae') &&
        device.metadata?.aeFuelType === EAEFuelType.FUEL_CONSTANTA &&
        !device.metadata,
    );

    if (foundAENoMetadata && foundAENoMetadata.length) {
      return (
        <Tooltip
          title={`${foundAENoMetadata.length} AE device(s) missing metadata`}
        >
          <WarningFilled style={{ color: '#faad14', marginLeft: 4 }} />
        </Tooltip>
      );
    }

    return false;
  };

  return (
    <React.Fragment>
      <input
        ref={uploadInputRef}
        type="file"
        accept={allowedImageMimes.join(',')}
        style={{ display: 'none' }}
        onChange={handleUploadImageChange}
      />
      {isMobile ? (
        <AssetMobilePage>
          <MobileAssetHeader
            title="Asset"
            onBack={() => navigate(-1)}
            action={
              isUserHasPermission([FEATURE + '.CREATE']) ? (
                <AssetMobileIconButton
                  type="button"
                  aria-label="Add asset"
                  onClick={handleCreateAsset}
                >
                  <PlusOutlined />
                </AssetMobileIconButton>
              ) : null
            }
          />

          <AssetMobileFilterRow>
            <AssetMobileSearch
              allowClear
              defaultValue={pageQueries.search}
              placeholder="Search by asset name"
              suffix={<SearchOutlined style={{ color: '#9facbf' }} />}
              onChange={(event) => handleSearch(event.target.value)}
            />
            <AssetMobileFilterButton
              type="button"
              aria-label="Open asset filters"
              onClick={() => setIsMobileFilterOpen(true)}
            >
              <RiEqualizer2Line size={20} />
            </AssetMobileFilterButton>
          </AssetMobileFilterRow>

          <AssetMobileList>
            {isLoading ? (
              <AssetMobileEmptyState>Loading assets...</AssetMobileEmptyState>
            ) : data.length === 0 ? (
              <AssetMobileEmptyState>No asset data found.</AssetMobileEmptyState>
            ) : (
              data.map((asset) => {
                const menuItems = getMenuItems(asset);

                return (
                  <AssetMobileCard key={asset.assetId}>
                    <AssetMobileCardMain>
                      <AssetMobileThumb>
                        <Image
                          src={asset.imageLink}
                          preview={false}
                          fallback="/images/flag-not-found.png"
                        />
                      </AssetMobileThumb>
                      <AssetMobileCardBody>
                        <AssetMobileCardTitle>{asset.name}</AssetMobileCardTitle>
                        <AssetMobileCardSubtitle>
                          {asset.company?.name || 'N/A'}
                        </AssetMobileCardSubtitle>
                        <AssetMobileInfoGrid>
                          <AssetMobileInfoItem>
                            <AssetMobileInfoLabel>
                              System Type
                            </AssetMobileInfoLabel>
                            <AssetMobileInfoValue>
                              {asset.assetType?.toUpperCase()}
                            </AssetMobileInfoValue>
                          </AssetMobileInfoItem>
                          <AssetMobileInfoItem>
                            <AssetMobileInfoLabel>
                              Total Device
                            </AssetMobileInfoLabel>
                            <AssetMobileInfoValue>
                              {asset.devices?.length || 0}
                              {_renderDeviceAlert(asset.devices)}
                            </AssetMobileInfoValue>
                          </AssetMobileInfoItem>
                          <AssetMobileInfoItem>
                            <AssetMobileInfoLabel>Status</AssetMobileInfoLabel>
                            <AssetMobileInfoValue>
                              <Tag
                                color={
                                  asset.status === 'active'
                                    ? 'success'
                                    : 'error'
                                }
                              >
                                {asset.status}
                              </Tag>
                            </AssetMobileInfoValue>
                          </AssetMobileInfoItem>
                        </AssetMobileInfoGrid>
                      </AssetMobileCardBody>
                      {menuItems && menuItems.length ? (
                        <Dropdown
                          menu={{
                            items: menuItems,
                            onClick: handleMenuClick(asset),
                          }}
                          trigger={['click']}
                        >
                          <AssetMobileMoreButton
                            type="button"
                            aria-label={`Open actions for ${asset.name}`}
                          >
                            <RiMore2Fill size={20} />
                          </AssetMobileMoreButton>
                        </Dropdown>
                      ) : (
                        <span />
                      )}
                    </AssetMobileCardMain>
                    {isUserHasPermission([FEATURE + '.DETAIL']) ? (
                      <AssetMobileDetailButton
                        type="button"
                        onClick={() => navigate('/asset/' + asset.assetId)}
                      >
                        See Detail Asset
                      </AssetMobileDetailButton>
                    ) : null}
                  </AssetMobileCard>
                );
              })
            )}
          </AssetMobileList>

          <AssetMobilePagination>
            <AssetMobilePaginationInfo>
              Showing {mobileStartResult} to {mobileEndResult} of {mobileTotal}{' '}
              results
            </AssetMobilePaginationInfo>
            <AssetMobilePaginationActions>
              <AssetMobilePageButton
                type="button"
                disabled={mobileCurrentPage <= 1}
                onClick={() => handleMobilePageChange(mobileCurrentPage - 1)}
                aria-label="Previous page"
              >
                <LeftOutlined />
              </AssetMobilePageButton>
              <AssetMobilePageButton type="button" $active>
                {mobileCurrentPage}
              </AssetMobilePageButton>
              {mobileCurrentPage < mobileTotalPages ? (
                <AssetMobilePageButton
                  type="button"
                  onClick={() => handleMobilePageChange(mobileCurrentPage + 1)}
                >
                  {mobileCurrentPage + 1}
                </AssetMobilePageButton>
              ) : null}
              <AssetMobilePageButton
                type="button"
                disabled={mobileCurrentPage >= mobileTotalPages}
                onClick={() => handleMobilePageChange(mobileCurrentPage + 1)}
                aria-label="Next page"
              >
                <RightOutlined />
              </AssetMobilePageButton>
            </AssetMobilePaginationActions>
          </AssetMobilePagination>
        </AssetMobilePage>
      ) : (
        <>
          <HeaderSection
            title={'Asset'}
            rightAction={[
              isUserHasPermission([FEATURE + '.CREATE']) ? (
                <Button type='primary' onClick={handleCreateAsset}>
                  Add Asset
                </Button>
              ) : (
                false
              ),
            ]}
          />

          <ListFilterRow
            key={`asset-filter-${isLoadingCompanies ? 'loading' : 'ready'}-${
              visibleCompanyFilter || 'empty'
            }`}
            filterValues={{
              search: pageQueries.search,
              status: pageQueries.filterStatus || 'all',
              filterData: pageQueries.filterArchived || 'default',
              company: visibleCompanyFilter,
            }}
            filters={[filterData]}
          />

          {isLoading || data.length === 0 ? (
            <AppTable
              isLoading={isLoading}
              keyId="assetId"
              columns={columns}
              data={data}
              pagination={pagination}
              onChangePage={changePage}
              onChangeLimit={changeLimit}
              // _table={{
              //   scroll:{ x: '100%' }
              // } as any}
            />
          ) : (
            <AssetContainer>
              {data.map((asset, i) => {
                const menuItems = getMenuItems(asset);

                return (
                  <Card key={i}>
                    <Image
                      src={asset.imageLink}
                      width={"100%"}
                      height={200}
                      style={{
                        objectFit: "cover",
                        background: "#f4f6f9",
                      }}
                      fallback="/images/flag-not-found.png"
                    />
                    <CardContent>
                      <TopInfo>
                        <div>
                          <AssetName>{asset.name}</AssetName>
                          <Company>
                            <span>{asset.company?.name}</span>
                          </Company>
                        </div>
                        {menuItems && menuItems.length ? (
                          <MoreButton>
                            <Dropdown
                              menu={{
                                items: menuItems,
                                onClick: handleMenuClick(asset),
                              }}
                            >
                              <RiMore2Fill size={16} />
                            </Dropdown>
                          </MoreButton>
                        ) : null}
                      </TopInfo>
                      <Info>
                        <div>
                          <Label>System Type</Label>
                          <Value>{asset.assetType.toUpperCase()}</Value>
                        </div>
                        <div>
                          <Label>Total Device</Label>
                          <Value>
                            <span>{asset.devices?.length}</span>
                            {_renderDeviceAlert(asset.devices)}
                          </Value>
                        </div>
                        <div>
                          <Label>Status</Label>
                          <Value>
                            <Tag
                              color={
                                asset.status === "active" ? "success" : "danger"
                              }
                            >
                              {asset.status.toUpperCase()}
                            </Tag>
                          </Value>
                        </div>
                      </Info>
                    </CardContent>
                    <Action>
                      <div onClick={() => navigate("/asset/" + asset.assetId)}>
                        See Detail Assets
                      </div>
                    </Action>
                  </Card>
                );
              })}
            </AssetContainer>
          )}
        </>
      )}

      <AssetMobileFilterDrawer
        title="Filter"
        placement="bottom"
        height="auto"
        open={isMobileFilterOpen}
        onClose={() => setIsMobileFilterOpen(false)}
      >
        <AssetMobileFilterForm>
          <AssetMobileFilterFields>
            <AssetMobileFilterField>
              Company
              <AssetMobileFilterSelect
                value={mobileFilterValues.company}
                loading={isLoadingCompanies}
                options={filterCompanyOptions}
                onChange={(value) =>
                  setMobileFilterValues((prev) => ({
                    ...prev,
                    company: String(value),
                  }))
                }
              />
            </AssetMobileFilterField>
            <AssetMobileFilterField>
              Status
              <AssetMobileFilterSelect
                value={mobileFilterValues.status}
                options={[
                  { value: 'all', label: 'All' },
                  { value: 'active', label: 'Active' },
                  { value: 'inactive', label: 'Inactive' },
                ]}
                onChange={(value) =>
                  setMobileFilterValues((prev) => ({
                    ...prev,
                    status: String(value),
                  }))
                }
              />
            </AssetMobileFilterField>
            {isUserHasPermission([FEATURE + '.DELETE']) ? (
              <AssetMobileFilterField>
                Filter Data
                <AssetMobileFilterSelect
                  value={mobileFilterValues.filterData}
                  options={[
                    { value: 'default', label: 'Default' },
                    { value: 'show', label: 'Include Archive Data' },
                  ]}
                  onChange={(value) =>
                    setMobileFilterValues((prev) => ({
                      ...prev,
                      filterData: String(value),
                    }))
                  }
                />
              </AssetMobileFilterField>
            ) : null}
          </AssetMobileFilterFields>
          <AssetMobileFilterActions>
            <AssetMobileFilterApplyButton
              type="primary"
              onClick={handleApplyMobileFilter}
            >
              Apply
            </AssetMobileFilterApplyButton>
          </AssetMobileFilterActions>
        </AssetMobileFilterForm>
      </AssetMobileFilterDrawer>

      <Modal
        title={
          forceDeleteId ? 'Force Delete Confirmation' : 'Delete Confirmation'
        }
        open={isModalDeleteOpen}
        onOk={deleteData}
        okButtonProps={{
          style: {
            background: 'red',
          },
        }}
        onCancel={handleCancelDelete}
      >
        {forceDeleteId ? (
          <p>
            Are you sure want to permanently delete this data? This data will be
            deleted and only can't be undo.
          </p>
        ) : (
          <p>
            Are you sure want to delete this data? This data will be archived
            and only User with restore permission can undo this action.
          </p>
        )}
      </Modal>

      <Modal
        title='Restore Confirmation'
        open={isModalRestoreOpen}
        onOk={restoreData}
        okButtonProps={{
          style: {
            background: 'blue',
          },
        }}
        onCancel={handleCancelDelete}
      >
        <p>Are you sure want to restore this data?.</p>
      </Modal>
    </React.Fragment>
  );
};

const AssetContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(1, 1fr);
  gap: 15px;

  @media (min-width: 600px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (min-width: 1200px) {
    grid-template-columns: repeat(4, 1fr);
  }
`;

const Card = styled.div`
  border-radius: 12px;
  border: 1px solid #dadada;
  box-shadow: 1px 1px 8px rgba(0, 0, 0, 0.05);
  overflow: hidden;
  position: relative;
`;

const CardContent = styled.div`
  padding: 12px;
`;

const AssetName = styled.div`
  font-size: 16px;
  font-weight: 600;
  margin-bottom: 8px;
`;

const Company = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  color: #98a0ac;
`;

const Info = styled.div`
  display: grid;
  margin-top: 20px;
  grid-template-columns: repeat(3, 1fr);
`;

const Label = styled.div`
  font-size: 11px;
  color: #98a0ac;
  margin-bottom: 4px;
`;

const Value = styled.div`
  font-size: 14px;
`;

const Action = styled.div`
  padding: 12px;
  padding-top: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  > div {
    cursor: pointer;
    width: 100%;
    border-radius: 200px;
    background-color: #eaecf0;
    padding: 12px 0;
    display: flex;
    align-items: center;
    justify-content: center;
  }
`;

const TopInfo = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const MoreButton = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 100px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

export default Asset;
